// File: frontend/src/pages/reports/AttendanceReport.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSchool } from '../../context/SchoolContext';
import { exportToPDF } from '../../utils/pdfExport';
import api from '../../utils/axios';

const AttendanceReportPage = () => {
  const { school } = useSchool();
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError(null);
        const res = await api.get('/api/v1/reports/attendance', {
          params: { month: selectedMonth, year: selectedYear }
        });
        if (res.data?.success) {
          setData(res.data.data);
        } else {
          setError(res.data?.message || 'Failed to load attendance report.');
        }
      } catch (err) {
        console.error('Error loading attendance report:', err);
        setError(err.response?.data?.message || err.message || 'Error loading attendance report.');
      } finally {
        setLoading(false);
      }
    };
    fetchReport();
  }, [selectedMonth, selectedYear]);

  /**
   * Export Attendance Report to PDF
   */
  const handleExportPDF = () => {
    if (!data || !data.classes) return;

    const monthNames = ['', 'January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    const title = `Attendance Report - ${monthNames[selectedMonth]} ${selectedYear}`;
    const headers = ['Class', 'Total Students', 'Present %', 'Absent %', 'Late %'];
    const rows = data.classes.map((c) => [
      `Class ${c.class_id}`,
      c.total_students || 0,
      `${c.present_percentage}%`,
      `${c.absent_percentage}%`,
      `${c.late_percentage}%`
    ]);

    exportToPDF(title, headers, rows, school?.name);
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Top Navbar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/reports" className="w-10 h-10 rounded-xl bg-slate-100 text-slate-700 hover:bg-slate-200 flex items-center justify-center font-bold transition">
              ←
            </Link>
            <div>
              <h1 className="font-bold text-slate-800 text-lg leading-tight">Monthly Attendance Report</h1>
              <p className="text-xs text-slate-500">Class-wise attendance percentages & analytics</p>
            </div>
          </div>

          <button
            onClick={handleExportPDF}
            disabled={loading || !data}
            className="bg-primary hover:bg-blue-600 text-white font-bold px-5 py-2.5 rounded-xl text-xs shadow-md transition disabled:opacity-50 flex items-center gap-2"
          >
            📄 Export PDF Report
          </button>
        </div>
      </header>

      {/* Main Body */}
      <main className="flex-1 max-w-7xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        
        {/* Month & Year Select Filters */}
        <div className="bg-white rounded-2xl p-6 border border-slate-200 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <h2 className="font-bold text-slate-900 text-base">Select Report Period</h2>
          <div className="flex items-center gap-3 w-full sm:w-auto">
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(parseInt(e.target.value, 10))}
              className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value="1">January</option>
              <option value="2">February</option>
              <option value="3">March</option>
              <option value="4">April</option>
              <option value="5">May</option>
              <option value="6">June</option>
              <option value="7">July</option>
              <option value="8">August</option>
              <option value="9">September</option>
              <option value="10">October</option>
              <option value="11">November</option>
              <option value="12">December</option>
            </select>

            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
              className="px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold"
            >
              <option value="2025">2025</option>
              <option value="2026">2026</option>
              <option value="2027">2027</option>
            </select>
          </div>
        </div>

        {error && (
          <div className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-2xl text-sm font-semibold">
            {error}
          </div>
        )}

        {/* Attendance Summary Table */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="p-6 border-b border-slate-100 flex items-center justify-between">
            <h2 className="font-bold text-slate-900 text-base">Class-wise Attendance Rates</h2>
            <span className="text-xs text-slate-400 font-semibold">{data?.classes?.length || 0} Classes Evaluated</span>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs font-sans">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200 text-slate-500 font-semibold uppercase">
                  <th className="py-3.5 px-6">Class</th>
                  <th className="py-3.5 px-6 text-center">Total Students</th>
                  <th className="py-3.5 px-6 text-center">Present %</th>
                  <th className="py-3.5 px-6 text-center">Absent %</th>
                  <th className="py-3.5 px-6 text-center">Late %</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-slate-800 font-medium">
                {loading ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-400 font-medium">Calculating attendance statistics...</td>
                  </tr>
                ) : !data?.classes || data.classes.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="py-12 text-center text-slate-400 font-medium">No attendance logs found for selected period.</td>
                  </tr>
                ) : (
                  data.classes.map((c) => (
                    <tr key={c.class_id} className="hover:bg-slate-50/80 transition">
                      <td className="py-3.5 px-6 font-bold text-slate-900 text-sm">Class {c.class_id}</td>
                      <td className="py-3.5 px-6 text-center font-bold text-slate-900 text-sm">{c.total_students}</td>
                      <td className="py-3.5 px-6 text-center font-bold text-emerald-600">{c.present_percentage}%</td>
                      <td className="py-3.5 px-6 text-center font-bold text-red-600">{c.absent_percentage}%</td>
                      <td className="py-3.5 px-6 text-center font-bold text-amber-600">{c.late_percentage}%</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      </main>
    </div>
  );
};

export default AttendanceReportPage;
